import { MatSelect } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { filter, finalize, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CommonApiService } from '../common-api.service';
import { CommonDropdownService } from '../common-dropdown.service';

/**
 * Encapsulates the searchable + paginated mat-select dropdown pattern that
 * was duplicated across many report components (city/doctor/job pickers).
 *
 * Each component owns its own instance, so per-instance state
 * (page counter, total, selected entity) is no longer shared on the
 * `CommonDropdownService` — fixing the latent bug where two dropdowns on
 * different reports would clobber each other's `*SearchPage` field.
 *
 * Usage:
 *
 *   doctor = new PaginatedDropdown(this.api, this.dropdownService, 'doctor');
 *
 *   ngOnInit() { this.doctor.init(); }
 *   ngOnDestroy() { this.doctor.destroy(); }
 *
 * Template:
 *
 *   <mat-select #ref [(value)]="doctor.selectedId"
 *               (openedChange)="doctor.attachScroll(ref)">
 *     <input class="select-input" placeholder="Search..." matInput
 *            (keydown)="$event.stopPropagation()"
 *            (keyup)="doctor.onSearch($event)">
 *     <mat-option [value]="0">All</mat-option>
 *     <mat-option *ngFor="let o of doctor.items" [value]="o.id">{{ o.name }}</mat-option>
 *   </mat-select>
 */
export class PaginatedDropdown {
  /** Page of items currently loaded into the dropdown. */
  public items: any[] = [];

  /**
   * Currently selected id (bound to `[(value)]`).
   * Left undefined by default so consumers that require an explicit pick
   * can validate. Pickers that have an "All" option should set
   * `selectedId = 0` after construction.
   */
  public selectedId: number | undefined;

  /** Optionally pre-populate the selected entity (kept pinned at top of list). */
  public selectedEntity: any[] = [];

  /** Total row count returned by the server, for "load more" cutoff. */
  public total = 0;

  /** Current page index sent to the API. */
  public page = 0;

  /** Page key used as a label by the underlying CommonDropdownService.
   *  Per-instance to avoid cross-component clobbering. */
  private readonly pageKey: string;

  private subs = new Subscription();

  constructor(
    private api: CommonApiService,
    private dropdownService: CommonDropdownService,
    /** Backend entity slug, e.g. 'doctor', 'area', 'joborder'. */
    private entityType: string,
    /** API list endpoint suffix; defaults to `${entityType}/list/display`. */
    private listRoute?: string,
  ) {
    this.pageKey = `_pdd_${entityType}_${Math.random().toString(36).slice(2, 8)}`;
  }

  /** Initial load — call from ngOnInit. */
  init(): void {
    const params = {
      pageIndex: this.page,
      pageSize: environment.defaultPageSizeOnScroll,
    };

    const sub = this.api.postAll(this.endpoint, params).pipe(
      tap((res: any) => {
        this.items.push(...res.items);
        this.total = res.rowCount;
      }),
      filter(() => this.selectedEntity.length !== 0),
      tap(() => {
        const id = this.selectedEntity[0].id;
        this.items = this.items.filter(it => it.id !== id);
        this.items.unshift(this.selectedEntity[0]);
      }),
      finalize(() => this.page++),
    ).subscribe();
    this.subs.add(sub);
  }

  /** Bound to the search input (keyup). */
  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const sub = this.dropdownService
      .updateSearchResults(value, this.entityType, this.items, this.selectedEntity, this.pageKey)
      .subscribe(result => {
        this.items = result.entityList;
        this.page = result.pageVariable;
      });
    this.subs.add(sub);
  }

  /**
   * Bound to mat-select's `(openedChange)` — wires a scroll listener that
   * fetches the next page when the user reaches the bottom of the panel.
   *
   * Mat-select destroys & re-creates its overlay panel on every open, so we
   * (re)attach a fresh listener on each open. When closing, `panel` is
   * undefined and we no-op.
   */
  attachScroll(matSelect: MatSelect): void {
    const panel = matSelect?.panel?.nativeElement as HTMLElement | undefined;
    if (!panel) {
      return;
    }

    panel.addEventListener('scroll', () => {
      const tolerance = 10;
      const reachedBottom = Math.floor(panel.scrollTop) + tolerance >=
        Math.floor(panel.scrollHeight) - Math.floor(panel.offsetHeight);
      if (!reachedBottom) {
        return;
      }
      if (this.items.length >= this.total) {
        return;
      }

      const params = {
        pageIndex: this.page,
        pageSize: environment.defaultPageSizeOnScroll,
      };
      const sub = this.api.postAll(this.endpoint, params).subscribe((res: any) => {
        const id = this.selectedEntity[0]?.id;
        const incoming = id ? res.items.filter((it: any) => it.id !== id) : res.items;
        this.items.push(...incoming);
      });
      this.subs.add(sub);
      this.page++;
    });
  }

  /** Tear down all in-flight subscriptions. Call from ngOnDestroy. */
  destroy(): void {
    this.subs.unsubscribe();
  }

  private get endpoint(): string {
    return this.listRoute || `${this.entityType}/list/display`;
  }
}
